import { useState, useMemo, useCallback, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { NavIcon, SidebarToggleButton, useBrandTokens } from '../../shell/shared';
import { useAppointmentsContext } from '../../shell/AppointmentsShellLayout';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qAppointmentProviders,
  qAppointmentProviderTypes,
  mapProviderRowToUI,
  type AppointmentProviderRow,
  type AppointmentProviderTypeRow,
} from '@helios/data-access';
import { ProviderDrawer } from './ProviderDrawer';

// ─── Types ───

export type ProviderType = 'Doctor' | 'Therapist' | 'Consultant';

export interface Provider {
  id: string;
  fullName: string;
  displayName: string;
  specialty: string;
  types: string[];
  email: string;
  mobile: string;
  active: boolean;
  avatarColor: string;
}

// ─── Helper: get initials ───

function getInitials(name: string): string {
  const parts = name
    .replace(/^Dr\.\s*/i, '')
    .trim()
    .split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] ?? '').toUpperCase();
}

// ─── Type chip colors ───

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  Doctor: { bg: '#fef3cd', color: '#856404' },
  Therapist: { bg: '#d1ecf1', color: '#0c5460' },
  Consultant: { bg: '#d4edda', color: '#155724' },
};

// ─── Type Chip ───

function TypeChip({ type }: { type: string }) {
  const c = TYPE_COLORS[type] ?? { bg: '#f3f4f6', color: '#374151' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: c.bg,
        color: c.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      {type}
    </span>
  );
}

// ─── Status Toggle ───

function StatusToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        transition: 'background 150ms ease',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      />
    </button>
  );
}

// ─── Avatar ───

function ProviderAvatar({ name, color }: { name: string; color: string }) {
  return (
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 'var(--radius-full)',
        background: `linear-gradient(135deg, ${color}, ${color}dd)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        flexShrink: 0,
      }}
    >
      {getInitials(name)}
    </div>
  );
}

// ─── Filter Pill ───

function FilterPill({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 36,
        padding: '0 var(--sp-4)',
        borderRadius: 'var(--radius-full)',
        background: selected ? 'var(--color-primary)' : 'var(--bg-surface)',
        color: selected ? '#fff' : 'var(--text-muted)',
        border: selected ? 'none' : '1px solid var(--border)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        cursor: 'pointer',
        transition: 'all 150ms ease',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

// ─── Providers Page ───

export function ProvidersPage() {
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const { collapsed, onToggleSidebar } = useAppointmentsContext();

  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: typeRows = [] } = usePowerSyncQuery<AppointmentProviderTypeRow>(
    qAppointmentProviderTypes(),
  );

  const providers: Provider[] = useMemo(
    () => providerRows.map((r) => mapProviderRowToUI(r, typeRows)),
    [providerRows, typeRows],
  );

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ProviderType | null>('Doctor');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Provider | null>(null);

  // Filtered providers
  const filtered = useMemo(() => {
    let result = providers;
    if (typeFilter) {
      result = result.filter((p) => p.types.includes(typeFilter));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) => p.fullName.toLowerCase().includes(q) || p.specialty.toLowerCase().includes(q),
      );
    }
    return result;
  }, [providers, search, typeFilter]);

  // Handlers
  const handleNewProvider = () => {
    setEditingProvider(null);
    setDrawerOpen(true);
  };

  const handleEditProvider = (provider: Provider) => {
    setEditingProvider(provider);
    setDrawerOpen(true);
  };

  const handleSaveProvider = useCallback(
    async (data: {
      fullName: string;
      displayName: string;
      specialty: string;
      types: string[];
      email: string;
      mobile: string;
      active: boolean;
      avatarColor: string;
    }) => {
      if (!db) return;
      const now = new Date().toISOString();

      if (editingProvider) {
        // Update provider
        await db.execute(
          `UPDATE appointment_providers SET
            full_name = ?, display_name = ?, specialty = ?,
            email = ?, mobile = ?, avatar_color = ?, active = ?,
            updated_at = ?
          WHERE id = ?`,
          [
            data.fullName,
            data.displayName || null,
            data.specialty || null,
            data.email || null,
            data.mobile || null,
            data.avatarColor,
            data.active ? 1 : 0,
            now,
            editingProvider.id,
          ],
        );

        // Sync types: delete all, then re-insert
        await db.execute(`DELETE FROM appointment_provider_types WHERE provider_id = ?`, [
          editingProvider.id,
        ]);
        for (const type of data.types) {
          await db.execute(
            `INSERT INTO appointment_provider_types
              (id, org_id, clinic_id, provider_id, type, created_at, updated_at)
            VALUES (?, ?, NULL, ?, ?, ?, ?)`,
            [crypto.randomUUID(), orgId, editingProvider.id, type, now, now],
          );
        }
      } else {
        // Insert new provider
        const id = crypto.randomUUID();
        await db.execute(
          `INSERT INTO appointment_providers
            (id, org_id, clinic_id, full_name, display_name, specialty,
             email, mobile, avatar_color, active, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            data.fullName,
            data.displayName || null,
            data.specialty || null,
            data.email || null,
            data.mobile || null,
            data.avatarColor,
            data.active ? 1 : 0,
            now,
            now,
          ],
        );

        // Insert types
        for (const type of data.types) {
          await db.execute(
            `INSERT INTO appointment_provider_types
              (id, org_id, clinic_id, provider_id, type, created_at, updated_at)
            VALUES (?, ?, NULL, ?, ?, ?, ?)`,
            [crypto.randomUUID(), orgId, id, type, now, now],
          );
        }
      }
      setDrawerOpen(false);
      setEditingProvider(null);
    },
    [db, editingProvider, orgId],
  );

  const handleToggleStatus = useCallback(
    async (provider: Provider) => {
      if (!db) return;
      await db.execute(`UPDATE appointment_providers SET active = ?, updated_at = ? WHERE id = ?`, [
        provider.active ? 0 : 1,
        new Date().toISOString(),
        provider.id,
      ]);
    },
    [db],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!db || !deleteTarget) return;
    // Delete provider types first, then provider
    await db.execute(`DELETE FROM appointment_provider_types WHERE provider_id = ?`, [
      deleteTarget.id,
    ]);
    await db.execute(`DELETE FROM appointment_providers WHERE id = ?`, [deleteTarget.id]);
    setDeleteTarget(null);
  }, [db, deleteTarget]);

  const handleTypeFilterClick = (type: ProviderType) => {
    setTypeFilter((prev) => (prev === type ? null : type));
  };

  // ─── Grid column template ───
  const gridCols = '40px 1fr 180px 1fr 80px 80px';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          {onToggleSidebar && (
            <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
              <h1
                style={{
                  fontSize: 'var(--font-size-xl2)',
                  fontWeight: 'var(--font-weight-semi)',
                  lineHeight: 'var(--line-height-xl2)',
                  letterSpacing: '-0.44px',
                  color: 'var(--text)',
                  margin: 0,
                }}
              >
                Providers
              </h1>
              <span
                style={{
                  height: 22,
                  padding: '0 var(--sp-2)',
                  borderRadius: 'var(--radius-xs)',
                  background: 'var(--color-warning-bg)',
                  color: 'var(--color-warning-text)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  lineHeight: '22px',
                  letterSpacing: '0.12px',
                }}
              >
                Admin only
              </span>
            </div>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                margin: '2px 0 0',
              }}
            >
              Manage clinicians who can be assigned to appointments
            </p>
          </div>
        </div>

        <Button variant="gradient" size="md" onClick={handleNewProvider}>
          + New Provider
        </Button>
      </div>

      {/* Search + Filters Bar */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        {/* Search Input */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 12,
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search provider name, specialty..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 40,
              padding: '0 var(--sp-4) 0 40px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-base)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
        </div>

        {/* Type Filter Pills */}
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <FilterPill
            label="Doctor"
            selected={typeFilter === 'Doctor'}
            onClick={() => handleTypeFilterClick('Doctor')}
          />
          <FilterPill
            label="Therapist"
            selected={typeFilter === 'Therapist'}
            onClick={() => handleTypeFilterClick('Therapist')}
          />
          <FilterPill
            label="Consultant"
            selected={typeFilter === 'Consultant'}
            onClick={() => handleTypeFilterClick('Consultant')}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 var(--sp-6)' }}>
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: 'var(--sp-3)',
            alignItems: 'center',
            padding: 'var(--sp-3) var(--sp-4)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span /> {/* avatar col */}
          <span style={thStyle}>PROVIDER</span>
          <span style={thStyle}>TYPES</span>
          <span style={thStyle}>CONTACT</span>
          <span style={thStyle}>STATUS</span>
          <span style={thStyle}>ACTIONS</span>
        </div>

        {/* Table Rows */}
        {filtered.map((provider) => (
          <div
            key={provider.id}
            style={{
              display: 'grid',
              gridTemplateColumns: gridCols,
              gap: 'var(--sp-3)',
              alignItems: 'center',
              padding: 'var(--sp-3) var(--sp-4)',
              borderBottom: '1px solid var(--border)',
              transition: 'background 100ms ease',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.background =
                'var(--bg-hover, rgba(0,0,0,0.02))';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.background = 'transparent';
            }}
          >
            {/* Avatar */}
            <ProviderAvatar name={provider.fullName} color={provider.avatarColor} />

            {/* Name + Specialty */}
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                  lineHeight: 'var(--line-height-base)',
                }}
              >
                {provider.fullName}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  lineHeight: 'var(--line-height-sm)',
                }}
              >
                {provider.specialty}
              </div>
            </div>

            {/* Types */}
            <div style={{ display: 'flex', gap: 'var(--sp-1)', flexWrap: 'wrap' }}>
              {provider.types.map((t) => (
                <TypeChip key={t} type={t} />
              ))}
            </div>

            {/* Contact */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                }}
              >
                <NavIcon name="mail" />
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {provider.email || '—'}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                }}
              >
                <NavIcon name="phone" />
                <span>{provider.mobile || '—'}</span>
              </div>
            </div>

            {/* Status Toggle */}
            <StatusToggle checked={provider.active} onChange={() => handleToggleStatus(provider)} />

            {/* Actions */}
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <button
                onClick={() => handleEditProvider(provider)}
                aria-label="Edit provider"
                style={actionBtnStyle}
              >
                <NavIcon name="edit" />
              </button>
              <button
                onClick={() => setDeleteTarget(provider)}
                aria-label="Delete provider"
                style={actionBtnStyle}
              >
                <NavIcon name="trash" />
              </button>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div
            style={{
              padding: 'var(--sp-12) var(--sp-6)',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-base)',
            }}
          >
            No providers found matching your search.
          </div>
        )}
      </div>

      {/* Provider Drawer */}
      <ProviderDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        brandTokens={brandTokens}
        provider={editingProvider}
        onSave={handleSaveProvider}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete Provider"
        maxWidth={420}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-3)' }}>
            <Button variant="outline" size="md" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              size="md"
              onClick={handleDeleteConfirm}
              style={{ background: 'var(--color-danger)', color: '#fff' }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p style={{ color: 'var(--text)', fontSize: 'var(--font-size-base)', margin: 0 }}>
          Are you sure you want to delete <strong>{deleteTarget?.fullName}</strong>? This action
          cannot be undone.
        </p>
      </Modal>
    </div>
  );
}

// ─── Shared styles ───

const thStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const actionBtnStyle: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 'var(--radius-md)',
  background: 'transparent',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 0,
};
